import { useState, useCallback, useRef, useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import type {
  ChatMessage,
  ChatResponseMeta,
  TransientEvent,
  SSEEventType,
} from "../../../types";
import type { ChunkDocuments, ChunkMetadata } from "../types";
import {
  createChatOnly,
  sendChatMessage,
  getChat,
  streamChatRecovery,
  getFeedback,
  feedbackLike,
  feedbackDislike,
  feedbackReset,
  truncateChat,
  updateChatSettings,
} from "../../../services/api";
import { addRecentChat } from "../../../services/recentChatsService";
import {
  getStreamErrorContent,
  getThrowableErrorContent,
  isStreamModelUnavailable,
  isThrowableModelUnavailable,
} from "../../../utils/llmAvailability";

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Estrae i riferimenti [CHUNK-XXX] dal contenuto (singoli [CHUNK-N] o multipli [CHUNK-251, CHUNK-250]) */
function getChunkRefs(content: string): string[] {
  const refs = new Set<string>();
  const bracketMatches = Array.from(content.matchAll(/\[([^\]]+)\]/g));
  for (const m of bracketMatches) {
    const inner = m[1];
    const chunkMatches = Array.from(inner.matchAll(/CHUNK-(\d+)/gi));
    for (const c of chunkMatches) {
      refs.add(`CHUNK-${c[1]}`);
    }
  }
  return Array.from(refs);
}

/** Filtra i documenti della sessione assegnando solo quelli citati nel contenuto del messaggio */
function documentsForMessage(
  content: string,
  sessionDocuments: Record<string, ChunkMetadata> | undefined
): ChunkDocuments | undefined {
  if (!sessionDocuments || typeof sessionDocuments !== "object")
    return undefined;
  const refs = getChunkRefs(content);
  if (refs.length === 0) return undefined;
  const out: ChunkDocuments = {};
  for (const key of refs) {
    const val = sessionDocuments[key];
    if (val !== undefined && val !== null) {
      out[key] = val;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

// Tipi di eventi transitori che vengono mostrati e poi rimossi
const TRANSIENT_TYPES: SSEEventType[] = [
  "GraphNode",
  "FunctionToolCallEvent",
  "FunctionToolResultEvent",
  "PartStartEventThinking",
  "PartStartEventToolCall",
  "ToolCallPartDelta",
  "ThinkingPartDelta",
];

// Tipi di eventi testuali finali
const TEXT_TYPES: SSEEventType[] = ["PartStartEventText", "TextPartDelta"];

const inFlightChatRequests = new Map<string, Promise<any>>();
const inFlightFeedbackRequests = new Map<string, Promise<{ items: any[] }>>();

function getOrCreateInFlight<T>(
  store: Map<string, Promise<T>>,
  key: string,
  factory: () => Promise<T>
): Promise<T> {
  const existing = store.get(key);
  if (existing) return existing;
  const created = factory().finally(() => {
    store.delete(key);
  });
  store.set(key, created);
  return created;
}

function mapToolName(toolName: unknown): string {
  if (typeof toolName !== "string" || !toolName.trim()) return "tool";
  const normalized = toolName.trim();
  const toolLabels: Record<string, string> = {
    search_knowledgebase_tool: "Ricerca nella base di dati",
    output_critic: "Valutazione della risposta",
  };
  return toolLabels[normalized] ?? normalized;
}

function parseMessageTimestamp(value: unknown): Date | undefined {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return undefined;
}

function resolveHistoryTimestamp(msg: any, parts: any[]): Date {
  const fromMessage = parseMessageTimestamp(msg?.timestamp);
  if (fromMessage) return fromMessage;
  for (const part of parts) {
    const fromPart = parseMessageTimestamp(part?.timestamp);
    if (fromPart) return fromPart;
  }
  return new Date();
}

export type UseChatOptions = {
  fromNewToCreated?: boolean;
  initialUserMessage?: string;
};

export function useChat(
  collectionName: string = "chunks",
  options?: UseChatOptions
) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoadingMessage, setIsLoadingMessage] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [transientEvents, setTransientEvents] = useState<TransientEvent[]>([]);
  const [feedbackMap, setFeedbackMap] = useState<Record<string, 1 | -1>>({});
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState("");
  const [shortAnswer, setShortAnswerState] = useState(false);
  const [useProModel, setUseProModelState] = useState(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentStreamingId = useRef<string | null>(null);
  const activeRecoveryChatIdRef = useRef<string | null>(null);
  const didSetInitialMessageRef = useRef(false);
  const loadChatRef = useRef<
    ((chatId: string, loading?: boolean) => Promise<void>) | null
  >(null);

  const { fromNewToCreated = false, initialUserMessage } = options ?? {};

  useEffect(() => {
    if (
      fromNewToCreated &&
      typeof initialUserMessage === "string" &&
      initialUserMessage.trim() &&
      !didSetInitialMessageRef.current
    ) {
      didSetInitialMessageRef.current = true;
      setMessages([
        {
          id: generateId(),
          role: "user",
          content: initialUserMessage.trim(),
          timestamp: new Date(),
        },
      ]);
    }
  }, [fromNewToCreated, initialUserMessage]);

  const clearTransientEvents = useCallback(() => {
    setTransientEvents([]);
  }, []);

  const addTransientEvent = useCallback(
    (type: SSEEventType, label: string | SSEEventType, detail?: string) => {
      const event: TransientEvent = {
        id: generateId(),
        type,
        label,
        detail,
      };
      setTransientEvents((prev) => [...prev.slice(-4), event]); // Mantieni solo gli ultimi 5
    },
    []
  );

  const updateLastMessage = useCallback((updates: Partial<ChatMessage>) => {
    setMessages((prev) =>
      prev.length
        ? [...prev.slice(0, -1), { ...prev[prev.length - 1], ...updates }]
        : prev
    );
  }, []);

  /** Allinea la UI al server o alla navigazione: mai PATCH. */
  const hydrateChatPreferences = useCallback(
    (prefs: { short_answer: boolean; use_pro_model: boolean }) => {
      setShortAnswerState(prefs.short_answer);
      setUseProModelState(prefs.use_pro_model);
    },
    []
  );

  /** Solo da azione utente (toggle): aggiorna stato e persiste se serve. */
  const persistShortAnswer = useCallback(
    async (value: boolean) => {
      const prev = shortAnswer;
      setShortAnswerState(value);
      if (sessionId && value !== prev) {
        try {
          await updateChatSettings(sessionId, { short_answer: value });
        } catch {
          // Non bloccare UX se il salvataggio fallisce
        }
      }
    },
    [sessionId, shortAnswer]
  );

  /** Solo da azione utente (toggle): aggiorna stato e persiste se serve. */
  const persistUseProModel = useCallback(
    async (value: boolean) => {
      const prev = useProModel;
      setUseProModelState(value);
      if (sessionId && value !== prev) {
        try {
          await updateChatSettings(sessionId, { use_pro_model: value });
        } catch {
          // Non bloccare UX se il salvataggio fallisce
        }
      }
    },
    [sessionId, useProModel]
  );

  const sendMessage = useCallback(
    async (
      content: string,
      options?: {
        short_answer?: boolean;
        use_pro_model?: boolean;
      }
    ) => {
      if (!content.trim() || isLoadingMessage) return;

      const resolvedOptions = {
        short_answer: options?.short_answer ?? shortAnswer,
        use_pro_model: options?.use_pro_model ?? useProModel,
      };

      // Nuova chat: solo createChatOnly + navigate. Il send-message lo fa la pagina di destinazione (streamResponseForMessage).
      if (!sessionId) {
        try {
          const { id: newChatId } = await createChatOnly(resolvedOptions);
          queryClient.invalidateQueries("chats");
          navigate(`/chat/${newChatId}`, {
            replace: false,
            state: {
              fromNewToCreated: true,
              lastMessage: content.trim(),
              short_answer: resolvedOptions.short_answer,
              use_pro_model: resolvedOptions.use_pro_model,
            },
          });
        } catch (err) {
          console.error("Create chat error:", err);
          toast.error("Errore creazione chat");
        }
        return;
      }

      // Chat esistente: aggiungi messaggio utente e avvia stream send-message

      const userMessage: ChatMessage = {
        id: generateId(),
        role: "user",
        content: content.trim(),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsLoadingMessage(true);

      const startTime = Date.now();
      const nextAssistantIndex = messages.length + 1;
      const assistantMessageId = `${sessionId}-${nextAssistantIndex}`;
      currentStreamingId.current = assistantMessageId;

      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: "assistant",
        content: "",
        isStreaming: true,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      try {
        let textStarted = false;
        let accumulatedText = "";
        let documents: ChunkDocuments = {};
        let streamErrorContent: string | null = null;
        let streamIsModelUnavailable = false;

        const streamGenerator = sendChatMessage(
          sessionId,
          content.trim(),
          resolvedOptions
        );

        for await (const event of streamGenerator) {
          const eventType = event.type as SSEEventType;
          const eventData = event.data as Record<string, unknown>;

          // Model availability / generic backend errors
          if (eventType === "error" || eventType === "model_unavailable") {
            streamIsModelUnavailable = isStreamModelUnavailable(
              eventType,
              eventData
            );
            streamErrorContent = getStreamErrorContent(streamIsModelUnavailable);
            clearTransientEvents();
            break;
          }

          // Eventi transitori
          if (TRANSIENT_TYPES.includes(eventType)) {
            let label: string = eventType;
            let detail: string | undefined;

            if (eventType === "GraphNode") {
              label = `Nodo: ${eventData.node_name || "unknown"}`;
            } else if (eventType === "FunctionToolCallEvent") {
              label = `Chiamata: ${mapToolName(eventData.tool_name)}`;
              detail =
                typeof eventData.args === "string"
                  ? eventData.args.slice(0, 100)
                  : undefined;
            } else if (eventType === "FunctionToolResultEvent") {
              label = `Risultato: ${mapToolName(eventData.tool_name)}`;
            } else if (
              eventType === "ThinkingPartDelta" ||
              eventType === "PartStartEventThinking"
            ) {
              label = "Ragionamento...";
              detail =
                typeof eventData.thinking === "string"
                  ? eventData.thinking.slice(0, 100)
                  : undefined;
            } else if (
              eventType === "ToolCallPartDelta" ||
              eventType === "PartStartEventToolCall"
            ) {
              label = `Tool: ${mapToolName(eventData.tool_name)}`;
            }

            addTransientEvent(eventType, label, detail);
            continue;
          }

          // Eventi testuali
          if (TEXT_TYPES.includes(eventType)) {
            if (!textStarted) {
              textStarted = true;
              clearTransientEvents(); // Rimuovi eventi transitori quando inizia il testo
            }

            const textContent = (eventData.content as string) || "";
            accumulatedText += textContent;

            updateLastMessage({ content: accumulatedText });
            continue;
          }

          // Ultimo evento: id definitivo del messaggio dal backend
          if (
            eventType === "final_message_id" &&
            eventData?.message_id &&
            typeof eventData.message_id === "string"
          ) {
            const finalId = eventData.message_id as string;
            updateLastMessage({ id: finalId });
            queryClient.invalidateQueries("chats");
            continue;
          }

          // Fine con risultato
          if (eventType === "GraphEndNode") {
            const result = eventData.result as Record<string, unknown>;
            if (result) {
              if (result.answer && typeof result.answer === "string") {
                accumulatedText = result.answer;
              }
              if (result.documents && typeof result.documents === "object") {
                documents = result.documents as ChunkDocuments;
              }
            }
            clearTransientEvents();
          }

          if (eventType === "EndEventFinalResult") {
            const result = eventData.result as Record<string, unknown>;
            if (result && result.answer && typeof result.answer === "string") {
              accumulatedText = result.answer;
            }
            clearTransientEvents();
          }
        }

        // Calculate response time
        const responseTime = (Date.now() - startTime) / 1000; // in seconds

        if (streamErrorContent !== null) {
          // A backend error event was received — show it instead of the accumulated text.
          updateLastMessage({
            content: streamErrorContent,
            isStreaming: false,
            isModelUnavailable: streamIsModelUnavailable,
            responseTime,
          });
        } else {
          // Finalizza messaggio (ultimo in lista)
          updateLastMessage({
            content: accumulatedText || "Nessuna risposta generata.",
            documents: Object.keys(documents).length > 0 ? documents : undefined,
            isStreaming: false,
            responseTime,
          });
        }
      } catch (error) {
        console.error("Stream error:", error);
        const responseTime = (Date.now() - startTime) / 1000; // in seconds
        const isModelUnavailable = isThrowableModelUnavailable(error);

        updateLastMessage({
          content: getThrowableErrorContent(error),
          isStreaming: false,
          isModelUnavailable,
          responseTime,
        });
      } finally {
        setIsLoadingMessage(false);
        currentStreamingId.current = null;
        clearTransientEvents();
        if (sessionId) {
          loadChat(sessionId, false);
        }
      }
    },
    [
      isLoadingMessage,
      sessionId,
      shortAnswer,
      useProModel,
      navigate,
      queryClient,
      addTransientEvent,
      clearTransientEvents,
      updateLastMessage,
    ]
  );

  /** Avvia lo stream send-message sulla chat (usato dopo create + navigate, messaggio già in lista). */
  const streamResponseForMessage = useCallback(
    async (
      chatId: string,
      content: string,
      options?: { short_answer?: boolean; use_pro_model?: boolean }
    ) => {
      if (!content.trim()) return;
      setSessionId(chatId);
      setIsLoadingMessage(true);
      const startTime = Date.now();
      const assistantMessageId = `${chatId}-1`;
      currentStreamingId.current = assistantMessageId;
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: "assistant",
        content: "",
        isStreaming: true,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      const resolvedOptions = {
        short_answer: options?.short_answer ?? shortAnswer,
        use_pro_model: options?.use_pro_model ?? useProModel,
      };

      try {
        let textStarted = false;
        let accumulatedText = "";
        let streamErrorContent: string | null = null;
        let streamIsModelUnavailable = false;

        const streamGenerator = sendChatMessage(
          chatId,
          content.trim(),
          resolvedOptions
        );

        for await (const event of streamGenerator) {
          const eventType = event.type as SSEEventType;
          const eventData = event.data as Record<string, unknown>;

          // Model availability / generic backend errors
          if (eventType === "error" || eventType === "model_unavailable") {
            streamIsModelUnavailable = isStreamModelUnavailable(
              eventType,
              eventData
            );
            streamErrorContent = getStreamErrorContent(streamIsModelUnavailable);
            clearTransientEvents();
            break;
          }

          if (TRANSIENT_TYPES.includes(eventType)) {
            let label: string = eventType;
            if (eventType === "GraphNode")
              label = `Nodo: ${eventData.node_name || "unknown"}`;
            else if (eventType === "FunctionToolCallEvent")
              label = `Chiamata: ${mapToolName(eventData.tool_name)}`;
            else if (eventType === "FunctionToolResultEvent")
              label = `Risultato: ${mapToolName(eventData.tool_name)}`;
            else if (
              eventType === "ThinkingPartDelta" ||
              eventType === "PartStartEventThinking"
            )
              label = "Ragionamento...";
            else if (
              eventType === "ToolCallPartDelta" ||
              eventType === "PartStartEventToolCall"
            )
              label = `Tool: ${mapToolName(eventData.tool_name)}`;
            addTransientEvent(eventType, label);
            continue;
          }

          if (TEXT_TYPES.includes(eventType)) {
            if (!textStarted) {
              textStarted = true;
              clearTransientEvents();
            }
            accumulatedText += (eventData.content as string) || "";
            updateLastMessage({ content: accumulatedText });
            continue;
          }

          if (eventType === "GraphEndNode" || eventType === "EndEventFinalResult") {
            const result = eventData.result as Record<string, unknown>;
            if (result?.answer && typeof result.answer === "string")
              accumulatedText = result.answer;
            clearTransientEvents();
          }
        }

        const responseTime = (Date.now() - startTime) / 1000;
        if (streamErrorContent !== null) {
          updateLastMessage({
            content: streamErrorContent,
            isStreaming: false,
            isModelUnavailable: streamIsModelUnavailable,
            responseTime,
          });
        } else {
          updateLastMessage({
            content: accumulatedText || "Nessuna risposta generata.",
            isStreaming: false,
            responseTime,
          });
        }
      } catch (error) {
        console.error("Stream error:", error);
        const isModelUnavailable = isThrowableModelUnavailable(error);

        updateLastMessage({
          content: getThrowableErrorContent(error),
          isStreaming: false,
          isModelUnavailable,
          responseTime: (Date.now() - startTime) / 1000,
        });
      } finally {
        setIsLoadingMessage(false);
        currentStreamingId.current = null;
        clearTransientEvents();
        queryClient.invalidateQueries("chats");
        loadChatRef.current?.(chatId, false);
      }
    },
    [shortAnswer, useProModel, addTransientEvent, clearTransientEvents, updateLastMessage, queryClient]
  );

  const resumeStreaming = useCallback(
    async (chatId: string, pendingLastMessage?: string) => {
      if (activeRecoveryChatIdRef.current === chatId) return;
      activeRecoveryChatIdRef.current = chatId;
      setSessionId(chatId);
      setIsLoadingMessage(true);

      const ensureResumedQuestionVisible = (question: string) => {
        const trimmed = question.trim();
        if (!trimmed) return;
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          const beforeLast = prev[prev.length - 2];
          const hasAsLastUser =
            last?.role === "user" && last.content.trim() === trimmed;
          const hasBeforeStreamingAssistant =
            last?.role === "assistant" &&
            last.isStreaming &&
            beforeLast?.role === "user" &&
            beforeLast.content.trim() === trimmed;
          if (hasAsLastUser || hasBeforeStreamingAssistant) return prev;

          const resumedUserMessage: ChatMessage = {
            id: generateId(),
            role: "user",
            content: trimmed,
            disableEdit: true,
            timestamp: new Date(),
          };

          if (last?.role === "assistant" && last.isStreaming) {
            return [...prev.slice(0, -1), resumedUserMessage, last];
          }
          return [...prev, resumedUserMessage];
        });
      };

      if (typeof pendingLastMessage === "string" && pendingLastMessage.trim()) {
        ensureResumedQuestionVisible(pendingLastMessage);
      }

      const assistantMessage: ChatMessage = {
        id: generateId(),
        role: "assistant",
        content: "",
        isStreaming: true,
        timestamp: new Date(),
      };
      setMessages((prev) => {
        if (prev.length > 0 && prev[prev.length - 1].isStreaming) return prev;
        return [...prev, assistantMessage];
      });

      try {
        let textStarted = false;
        let accumulatedText = "";
        let documents: ChunkDocuments = {};
        let streamErrorContent: string | null = null;
        let streamIsModelUnavailable = false;
        const streamGenerator = streamChatRecovery(chatId);

        for await (const event of streamGenerator) {
          const eventType = event.type as SSEEventType | "resume_question";
          const eventData = event.data as Record<string, unknown>;

          // Model availability / generic backend errors
          if (eventType === "error" || eventType === "model_unavailable") {
            streamIsModelUnavailable = isStreamModelUnavailable(
              eventType,
              eventData
            );
            streamErrorContent = getStreamErrorContent(streamIsModelUnavailable);
            clearTransientEvents();
            break;
          }

          if (eventType === "resume_question") {
            const resumedQuestion = eventData?.question;
            if (typeof resumedQuestion === "string" && resumedQuestion.trim()) {
              ensureResumedQuestionVisible(resumedQuestion);
            }
            continue;
          }

          if (TRANSIENT_TYPES.includes(eventType as SSEEventType)) {
            let label: string = eventType;
            let detail: string | undefined;

            if (eventType === "GraphNode") {
              label = `Nodo: ${eventData.node_name || "unknown"}`;
            } else if (eventType === "FunctionToolCallEvent") {
              label = `Chiamata: ${mapToolName(eventData.tool_name)}`;
              detail =
                typeof eventData.args === "string"
                  ? eventData.args.slice(0, 100)
                  : undefined;
            } else if (eventType === "FunctionToolResultEvent") {
              label = `Risultato: ${mapToolName(eventData.tool_name)}`;
            } else if (
              eventType === "ThinkingPartDelta" ||
              eventType === "PartStartEventThinking"
            ) {
              label = "Ragionamento...";
              detail =
                typeof eventData.thinking === "string"
                  ? eventData.thinking.slice(0, 100)
                  : undefined;
            } else if (
              eventType === "ToolCallPartDelta" ||
              eventType === "PartStartEventToolCall"
            ) {
              label = `Tool: ${mapToolName(eventData.tool_name)}`;
            }

            addTransientEvent(eventType as SSEEventType, label, detail);
            continue;
          }

          if (TEXT_TYPES.includes(eventType as SSEEventType)) {
            if (!textStarted) {
              textStarted = true;
              clearTransientEvents();
            }
            const textContent = (eventData.content as string) || "";
            accumulatedText += textContent;
            updateLastMessage({ content: accumulatedText });
            continue;
          }

          if (
            eventType === "final_message_id" &&
            eventData?.message_id &&
            typeof eventData.message_id === "string"
          ) {
            updateLastMessage({ id: eventData.message_id as string });
            queryClient.invalidateQueries("chats");
            continue;
          }

          if (eventType === "GraphEndNode") {
            const result = eventData.result as Record<string, unknown>;
            if (result) {
              if (result.answer && typeof result.answer === "string") {
                accumulatedText = result.answer;
              }
              if (result.documents && typeof result.documents === "object") {
                documents = result.documents as ChunkDocuments;
              }
            }
            clearTransientEvents();
          }

          if (eventType === "EndEventFinalResult") {
            const result = eventData.result as Record<string, unknown>;
            if (result && result.answer && typeof result.answer === "string") {
              accumulatedText = result.answer;
            }
            clearTransientEvents();
          }
        }

        if (streamErrorContent !== null) {
          updateLastMessage({
            content: streamErrorContent,
            isStreaming: false,
            isModelUnavailable: streamIsModelUnavailable,
          });
        } else {
          updateLastMessage({
            content: accumulatedText || "Nessuna risposta generata.",
            documents: Object.keys(documents).length > 0 ? documents : undefined,
            isStreaming: false,
          });
        }
      } catch (error) {
        console.error("Recovery stream error:", error);
        const isModelUnavailable = isThrowableModelUnavailable(error);

        updateLastMessage({
          content: getThrowableErrorContent(error),
          isStreaming: false,
          isModelUnavailable,
        });
      } finally {
        try {
          await loadChat(chatId, false);
        } catch {
          // Keep UI responsive even if post-recovery reload fails.
        }
        setIsLoadingMessage(false);
        clearTransientEvents();
        activeRecoveryChatIdRef.current = null;
      }
    },
    [addTransientEvent, clearTransientEvents, queryClient, updateLastMessage]
  );

  const loadChat = useCallback(
    async (chatId: string, loading: boolean = true) => {
      try {
        loading && fromNewToCreated && setIsLoadingChat(true);
        const session = await getOrCreateInFlight(
          inFlightChatRequests,
          chatId,
          () => getChat(chatId)
        );

        // Converti history in ChatMessage[]
        const loadedMessages: ChatMessage[] = [];

        if (!session.history || !Array.isArray(session.history)) {
          console.warn("Session history is empty or invalid");
          setMessages([]);
          setSessionId(chatId);
          const s = session as {
            title?: string;
            create_date?: string;
            created_at?: string;
          };
          const createDate =
            s?.create_date ?? s?.created_at ?? new Date().toISOString();
          addRecentChat({
            id: chatId,
            title: s?.title ?? null,
            create_date:
              typeof createDate === "string"
                ? createDate
                : new Date(createDate).toISOString(),
          });
          return;
        }

        for (const msg of session.history.filter(
          (h: any) => h.final_answer || h.kind === "request"
        )) {
          const msgKind = (msg.kind || "").toLowerCase();
          const parts = msg.parts || [];

          // Se è una request, cerca user-prompt (escludendo i prompt interni del researcher)
          if (msgKind === "request") {
            if ((msg as any).internal_researcher_prompt) {
              // Prompt interno per il Researcher: non mostrarlo come messaggio utente
              continue;
            }

            for (const part of parts) {
              const partKind = (part.part_kind || "").toLowerCase();
              if (partKind === "user-prompt" && part.content) {
                loadedMessages.push({
                  id: msg.message_id,
                  role: "user",
                  content: part.content,
                  timestamp: resolveHistoryTimestamp(msg, parts),
                });
                break; // Solo un messaggio user per request
              }
            }
          }
          // Se è una response, cerca text o thinking
          else if (msgKind === "response") {
            let textContent = "";
            let thinkingContent = "";

            for (const part of parts) {
              const partKind = (part.part_kind || "").toLowerCase();
              if (typeof part.content === "string") {
                if (partKind === "text") {
                  textContent += part.content;
                } else if (partKind === "thinking") {
                  thinkingContent += part.content;
                } else if (partKind === "" && part.content) {
                  // Part senza part_kind, probabilmente text
                  textContent += part.content;
                }
              }
            }

            // Usa text se disponibile, altrimenti thinking come fallback
            const finalContent = textContent || thinkingContent;
            if (finalContent.trim()) {
              const documents = documentsForMessage(
                finalContent,
                session.documents
              );
              const meta = msg.meta as ChatResponseMeta | undefined;
              loadedMessages.push({
                id: msg.message_id,
                role: "assistant",
                content: finalContent,
                documents,
                timestamp: resolveHistoryTimestamp(msg, parts),
                responseTime:
                  meta?.elapsed_ms != null ? meta.elapsed_ms / 1000 : undefined,
                responseMeta: meta,
              });
            }
          }
        }

        setMessages(loadedMessages);
        setSessionId(chatId);
        try {
          const { items } = await getOrCreateInFlight(
            inFlightFeedbackRequests,
            chatId,
            () => getFeedback(chatId)
          );
          const map: Record<string, 1 | -1> = {};
          for (const it of items || []) {
            if (it.value === 1 || it.value === -1)
              map[it.messageId] = it.value as 1 | -1;
          }
          setFeedbackMap(map);
        } catch {
          setFeedbackMap({});
        }
        const s = session as {
          title?: string;
          create_date?: string;
          created_at?: string;
        };
        const createDate =
          s?.create_date ?? s?.created_at ?? new Date().toISOString();
        addRecentChat({
          id: chatId,
          title: s?.title ?? null,
          create_date:
            typeof createDate === "string"
              ? createDate
              : new Date(createDate).toISOString(),
        });
      } catch (error) {
        console.error("Failed to load chat:", error);
      } finally {
        fromNewToCreated && loading && setIsLoadingChat(false);
      }
    },
    [resumeStreaming]
  );

  useEffect(() => {
    loadChatRef.current = loadChat;
  }, [loadChat]);

  const newChat = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setTransientEvents([]);
    setFeedbackMap({});
    setEditingMessageId(null);
    setEditingContent("");
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  const handleRetry = useCallback(
    async (chatId: string, aiMessageId: string) => {
      const idx = messages.findIndex((m) => m.id === aiMessageId);
      if (idx <= 0 || messages[idx - 1].role !== "user") return;
      const userMsg = messages[idx - 1];
      const userContent = userMsg.content;
      try {
        await truncateChat(chatId, userMsg.id);
        await loadChat(chatId, false);
        await sendMessage(userContent);
        await loadChat(chatId, false);
      } catch (err: any) {
        toast.error(err?.response?.data?.detail ?? "Errore riprova");
      }
    },
    [messages, loadChat, sendMessage]
  );

  const handleStartEdit = useCallback((messageId: string, content: string) => {
    setEditingMessageId(messageId);
    setEditingContent(content);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setEditingContent("");
  }, []);

  const handleSendFromEdit = useCallback(
    async (content: string) => {
      if (!sessionId || !editingMessageId || !content.trim()) return;
      try {
        await truncateChat(sessionId, editingMessageId);
        setEditingMessageId(null);
        setEditingContent("");
        await loadChat(sessionId, false);
        await sendMessage(content.trim());
        await loadChat(sessionId, false);
        queryClient.invalidateQueries("chats");
      } catch (err: any) {
        toast.error(err?.response?.data?.detail ?? "Errore modifica");
      }
    },
    [sessionId, editingMessageId, loadChat, queryClient, sendMessage]
  );

  const handleFeedback = useCallback(
    async (
      chatId: string,
      messageId: string,
      action: "like" | "dislike" | "reset",
      comment?: string | null
    ) => {
      try {
        if (action === "like") {
          await feedbackLike(chatId, messageId, comment);
          toast.success("Like aggiunto");
        } else if (action === "dislike") {
          await feedbackDislike(chatId, messageId, comment);
          toast.success("Dislike aggiunto");
        } else {
          await feedbackReset(chatId, messageId);
          toast.success("Feedback rimosso");
        }
        const { items } = await getFeedback(chatId);
        const map: Record<string, 1 | -1> = {};
        for (const it of items || []) {
          if (it.value === 1 || it.value === -1)
            map[it.messageId] = it.value as 1 | -1;
        }
        setFeedbackMap(map);
      } catch (err: any) {
        toast.error(err?.response?.data?.detail ?? "Errore feedback");
      }
    },
    []
  );

  return {
    messages,
    sessionId,
    feedbackMap,
    isLoadingMessage,
    isLoadingChat,
    transientEvents,
    sendMessage,
    loadChat,
    newChat,
    streamResponseForMessage,
    handleFeedback,
    handleRetry,
    editingMessageId,
    editingContent,
    setEditingContent,
    handleStartEdit,
    handleSendFromEdit,
    cancelEdit,
    resumeStreaming,
    shortAnswer,
    useProModel,
    hydrateChatPreferences,
    persistShortAnswer,
    persistUseProModel,
  };
}
